// Builds the six-week programme view from the tests' schedules based on location fields.
import dayjs from 'dayjs';
import { dayFromToday, fmtDay, testId } from '../epmHelpers';
import { formatTestCardId } from '../../../utils/converter';

export const LANE_CAPS = { PDP: 2, PLP: 1, Basket: 2, Checkout: 2, Search: 1 };
const WEEKS = 6;
const WINDOW_DAYS = WEEKS * 7;

/** Returns true if two CSS component selectors overlap */
export const sharesComponent = (a, b) => !!a && !!b && (a === b || a.startsWith(b) || b.startsWith(a));

/**
 * Resolves start day and end day offsets (in days relative to today).
 */
export const resolveTestSchedule = (test) => {
    const today = dayjs().startOf('day');
    const startDay = dayjs(test?.schedule?.date_time).startOf('day').diff(today, 'day');
    const endDay = dayjs(test?.schedule?.end_date_time).startOf('day').diff(today, 'day');

    return {
        startDay,
        endDay,
    };
};

const barType = (resolvedSchedule, hasConflict) => {
    // 1. start & end date between current date -> solid
    if (resolvedSchedule.startDay <= 0 && resolvedSchedule.endDay >= 0) {
        return 'live';
    }

    // 2. future start date conflicting with other test -> red dashed bar
    if (resolvedSchedule.startDay > 0 && hasConflict) {
        return 'conflict';
    }

    // 3. start date in future & not conflicting with other test dates -> simple dashed bar
    if (resolvedSchedule.startDay > 0 && !hasConflict) {
        return 'scheduled';
    }

    return hasConflict ? 'conflict' : 'scheduled';
};

const barNote = (type, resolvedSchedule) => {
    if (resolvedSchedule.note) return resolvedSchedule.note;
    if (type === 'live') {
        const total = Math.max(1, resolvedSchedule.endDay - resolvedSchedule.startDay);
        const elapsed = Math.min(total, Math.max(1, 1 - resolvedSchedule.startDay));
        return `${elapsed}/${total}`;
    }
    if (type === 'conflict') return 'conflict';
    return '';
};

export const buildSchedule = (tests, options = {}, legacyOptions = {}) => {
    const opts = (options && typeof options === 'object' && ('selectedLocationKeys' in options || 'testLocations' in options))
        ? options
        : (legacyOptions && typeof legacyOptions === 'object' ? legacyOptions : options || {});

    const {
        selectedLocationKeys = [],
        testLocations = [],
    } = opts;

    const weekday = (dayFromToday(0).day() + 6) % 7; // Monday = 0
    const windowStart = -weekday - 7; // last week's Monday, as a day offset
    const toWeeks = (day) => Math.min(WEEKS, Math.max(0, (day - windowStart) / 7));

    // Include tests in LIVE and SCHEDULED status that have both start and end date
    const targetTests = (tests || []).filter((test) =>
        (test.status === 'LIVE' || test.status === 'SCHEDULED')
        && Boolean(test?.schedule?.date_time)
        && Boolean(test?.schedule?.end_date_time)
    );

    const resolvedList = targetTests.map((test) => {
        const schedule = resolveTestSchedule(test);
        const location = test?.ideation?.location || test?.location || {};
        return {
            test,
            id: String(testId(test)),
            key: formatTestCardId(test),
            location,
            status: test.status,
            ...schedule,
        };
    });

    let targetKeys = Array.isArray(selectedLocationKeys) ? selectedLocationKeys : [];
    if (targetKeys.length === 0 && testLocations?.length > 0) {
        targetKeys = [testLocations[0]?.key].filter(Boolean);
    }

    const lanes = [];

    targetKeys.forEach((fieldKey) => {
        const fieldDef = (testLocations || []).find((l) => l.key === fieldKey) || { key: fieldKey, label: fieldKey };

        // Collect distinct non-empty values for this location field
        const valueMap = new Map();
        resolvedList.forEach((item) => {
            const rawVal = item.location?.[fieldKey];
            if (typeof rawVal === 'string' && rawVal.trim()) {
                const trimmed = rawVal.trim();
                const lower = trimmed.toLowerCase();
                if (!valueMap.has(lower)) {
                    valueMap.set(lower, trimmed);
                }
            }
        });

        const distinctValues = Array.from(valueMap.values());

        distinctValues.forEach((valName) => {
            const valLower = valName.toLowerCase();
            const laneTests = resolvedList.filter((item) => {
                const itemVal = (item.location?.[fieldKey] || '').trim().toLowerCase();
                return itemVal === valLower;
            });

            const displayName = targetKeys.length > 1
                ? `${fieldDef.label}: ${valName}`
                : valName;

            const bars = laneTests
                .map((item) => {
                    const { id, key, status, startDay, endDay } = item;
                    const start = toWeeks(startDay);
                    const span = toWeeks(endDay) - start;

                    // Within each lane: tests conflict if their dates overlap within the same lane
                    const hasConflict = laneTests.some((other) =>
                        other.id !== id && (other.startDay < endDay && other.endDay > startDay)
                    );

                    const type = barType(item, hasConflict);
                    const note = barNote(type, item);

                    return {
                        id,
                        key,
                        status,
                        note,
                        start,
                        span,
                        type,
                        startDay,
                        endDay,
                    };
                })
                .filter((bar) => bar.span > 0);

            const cap = LANE_CAPS[valName] || LANE_CAPS[valName.toUpperCase()] || 2;
            const live = bars.filter((bar) => bar.type === 'live').length;

            lanes.push({
                name: displayName,
                fieldKey,
                valName,
                bars,
                live,
                capacity: `${live}/${cap}`,
                cap,
            });
        });
    });

    // Don't show the lanes if they don't have any test to show in current 6 weeks span
    const visibleLanes = lanes.filter((lane) => lane.bars.length > 0);

    // Order: Lanes with max test in schedule and live status in the current 6 weeks span will be on top
    visibleLanes.sort((a, b) => {
        if (b.bars.length !== a.bars.length) {
            return b.bars.length - a.bars.length;
        }
        return a.name.localeCompare(b.name);
    });

    // A lane is over capacity when a booked test starts while the lane is full
    const warnings = visibleLanes.flatMap((lane) => lane.bars
        .filter((bar) => bar.type === 'scheduled' || bar.type === 'conflict')
        .map((bar) => ({
            lane: lane.name,
            day: bar.startDay,
            running: lane.bars.filter((other) =>
                other.startDay <= bar.startDay && other.endDay > bar.startDay).length,
        }))
        .filter(({ lane: name, running }) => running >= (LANE_CAPS[name] || LANE_CAPS[name.toUpperCase()] || 2)));

    const capacityItems = visibleLanes.map((lane) => {
        const cap = lane.cap;
        const warning = warnings.find((w) => w.lane === lane.name);
        if (warning) return { text: `${lane.name} ${warning.running}/${cap} on ${fmtDay(warning.day)}`, warn: true };
        return { text: `${lane.name} ${lane.capacity}${lane.live >= cap ? ' — full' : ''}`, warn: false };
    });

    const allBars = visibleLanes.flatMap((lane) => lane.bars);
    const totalCap = visibleLanes.reduce((sum, lane) => sum + lane.cap, 0);

    // Count unique tests by test status (live vs scheduled) in the current 6-week window
    const uniqueVisibleTestsMap = new Map();
    visibleLanes.forEach((lane) => {
        lane.bars.forEach((bar) => {
            if (!uniqueVisibleTestsMap.has(bar.id)) {
                uniqueVisibleTestsMap.set(bar.id, bar);
            }
        });
    });
    const uniqueVisibleTests = Array.from(uniqueVisibleTestsMap.values());
    const liveCount = uniqueVisibleTests.filter((b) => b.status === 'LIVE').length;
    const scheduledCount = uniqueVisibleTests.filter((b) => b.status === 'SCHEDULED').length;

    return {
        weeks: Array.from({ length: WEEKS }, (_, index) => fmtDay(windowStart + index * 7)),
        todayFraction: (0 - windowStart) / WINDOW_DAYS,
        lanes: visibleLanes,
        counts: {
            live: liveCount,
            scheduled: scheduledCount,
            proposed: 0,
            conflicts: allBars.filter((bar) => bar.type === 'conflict').length,
        },
        capacityItems,
        firstWarning: warnings[0],
        totalCap,
    };
};

/** CSS left for a marker at `fraction` of a track that sits after a fixed label column. */
export const trackLeft = (fraction, labelWidth) => `calc(${fraction * 100}% + ${labelWidth * (1 - fraction)}px)`;
